/* Shared helpers for "paste HTML code -> animated embed".
   No React / Tiptap imports here, so RichContent (the read-only display) can use this file too. */

export const MAX_EMBED_CHARS = 500_000;          // ~500 KB of pasted code
export const EMBED_THEME = 'auto';               // 'auto' follows the visitor's OS dark mode · 'light' · 'dark'
const STRICT_CSP = true;                         // blocks network calls from embedded code (CDNs below still load)

/* ── storage: the code lives base64-encoded in a data attribute, so HTML sanitisers and
      attribute-escaping can never mangle it ─────────────────────────────────────────── */
export const encodeCode = (str) => {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

export const decodeCode = (b64) => {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return '';
  }
};

export const isHtmlFile = (f) => /\.html?$/i.test(f.name) || f.type === 'text/html';

/* ── Does this pasted text look like an HTML / SVG widget (rather than ordinary text)? ──
      Plain static <svg> with no styles/classes is left alone: the editor's existing
      SVG-to-image path still handles it. ────────────────────────────────────────────── */
const DYNAMIC = /<(?:style|script|animate\w*|set|canvas|button|input|iframe|foreignObject)[\s>]|@keyframes|var\(--|class\s*=\s*["'][^"']*\b(?:ts|th|c-[a-z]+|node|arr|leader)\b/i;

export function looksLikeHtmlCode(text) {
  const t = (text || '').trim();
  if (t.length < 20 || t[0] !== '<' || t[t.length - 1] !== '>') return null;
  if (!/^<\s*(?:!doctype|html|style|svg|div|section|article|main|figure|canvas|table|button|script|p|h[1-6]|span|ul|ol)\b/i.test(t)) return null;
  const fullDoc = /^<\s*(?:!doctype|html)\b/i.test(t);
  return fullDoc || DYNAMIC.test(t) ? t : null;
}

/* ── CSS shim: widgets written for claude.ai use its CSS variables and SVG classes
      (t, ts, th, box, arr, leader, c-blue, c-teal ...). Define them here so the same
      code looks right in your site and in a downloaded .htm. ───────────────────────── */
const RAMPS = {
  purple: { 50: '#EEEDFE', 100: '#CECBF6', 200: '#AFA9EC', 400: '#7F77DD', 600: '#534AB7', 800: '#3C3489', 900: '#26215C' },
  teal:   { 50: '#E1F5EE', 100: '#9FE1CB', 200: '#5DCAA5', 400: '#1D9E75', 600: '#0F6E56', 800: '#085041', 900: '#04342C' },
  coral:  { 50: '#FAECE7', 100: '#F5C4B3', 200: '#F0997B', 400: '#D85A30', 600: '#993C1D', 800: '#712B13', 900: '#4A1B0C' },
  pink:   { 50: '#FBEAF0', 100: '#F4C0D1', 200: '#ED93B1', 400: '#D4537E', 600: '#993556', 800: '#72243E', 900: '#4B1528' },
  gray:   { 50: '#F1EFE8', 100: '#D3D1C7', 200: '#B4B2A9', 400: '#888780', 600: '#5F5E5A', 800: '#444441', 900: '#2C2C2A' },
  blue:   { 50: '#E6F1FB', 100: '#B5D4F4', 200: '#85B7EB', 400: '#378ADD', 600: '#185FA5', 800: '#0C447C', 900: '#042C53' },
  green:  { 50: '#EAF3DE', 100: '#C0DD97', 200: '#97C459', 400: '#639922', 600: '#3B6D11', 800: '#27500A', 900: '#173404' },
  amber:  { 50: '#FAEEDA', 100: '#FAC775', 200: '#EF9F27', 400: '#BA7517', 600: '#854F0B', 800: '#633806', 900: '#412402' },
  red:    { 50: '#FCEBEB', 100: '#F7C1C1', 200: '#F09595', 400: '#E24B4A', 600: '#A32D2D', 800: '#791F1F', 900: '#501313' },
};

const LIGHT_VARS =
  '--font-sans:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;--font-voice:Georgia,serif;--font-mono:ui-monospace,Menlo,Consolas,monospace;' +
  '--text-primary:#1f1e1d;--text-secondary:#5f5e5a;--text-muted:#888780;' +
  '--surface-0:#faf9f5;--surface-1:#f5f4ee;--surface-2:#ffffff;' +
  '--border:rgba(31,30,29,.15);--border-strong:rgba(31,30,29,.3);--border-stronger:rgba(31,30,29,.5);' +
  '--bg-accent:#E6F1FB;--text-accent:#0C447C;--border-accent:#85B7EB;' +
  '--bg-danger:#FCEBEB;--text-danger:#791F1F;--border-danger:#F09595;' +
  '--bg-success:#EAF3DE;--text-success:#27500A;--border-success:#97C459;' +
  '--bg-warning:#FAEEDA;--text-warning:#633806;--border-warning:#EF9F27;' +
  '--radius:8px;--t:var(--text-secondary);--s:var(--text-secondary);--p:var(--text-primary);--bg2:var(--surface-1);--b:var(--border);';

const DARK_VARS =
  '--text-primary:#f1efe8;--text-secondary:#b4b2a9;--text-muted:#888780;' +
  '--surface-0:#1f1e1d;--surface-1:#2c2c2a;--surface-2:#262624;' +
  '--border:rgba(241,239,232,.15);--border-strong:rgba(241,239,232,.3);--border-stronger:rgba(241,239,232,.5);' +
  '--bg-accent:#0C447C;--text-accent:#B5D4F4;--border-accent:#378ADD;' +
  '--bg-danger:#791F1F;--text-danger:#F7C1C1;--border-danger:#E24B4A;' +
  '--bg-success:#27500A;--text-success:#C0DD97;--border-success:#639922;' +
  '--bg-warning:#633806;--text-warning:#FAC775;--border-warning:#BA7517;';

const COMMON_CSS =
  'html,body{margin:0;background:transparent}' +
  'body{display:flow-root;font-family:var(--font-sans);font-size:16px;line-height:1.7;color:var(--text-primary)}' +
  'h1{font-size:22px;font-weight:500}h2{font-size:18px;font-weight:500}h3{font-size:16px;font-weight:500}' +
  'svg{max-width:100%}svg text{font-family:var(--font-sans)}' +
  '.t{font-size:14px;fill:var(--text-primary)}.ts{font-size:12px;fill:var(--text-secondary)}.th{font-size:14px;font-weight:500;fill:var(--text-primary)}' +
  '.box{fill:var(--surface-1);stroke:var(--border-strong)}' +
  '.arr{stroke:var(--text-secondary);stroke-width:1.5;fill:none}' +
  '.leader{stroke:var(--text-muted);stroke-width:.5;stroke-dasharray:3 3;fill:none}' +
  '.node{cursor:pointer}.node:hover{opacity:.85}' +
  '.sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}' +
  'button,input,select,textarea{font-family:inherit}';

const rampRules = (dark) =>
  Object.entries(RAMPS).map(([n, r]) => {
    const fill = dark ? r[800] : r[50];
    const stroke = dark ? r[200] : r[600];
    const title = dark ? r[100] : r[800];
    const sub = dark ? r[200] : r[600];
    return (
      `.c-${n}>rect,.c-${n}>circle,.c-${n}>ellipse,.c-${n}>polygon,rect.c-${n},circle.c-${n},ellipse.c-${n}{fill:${fill};stroke:${stroke}}` +
      `.c-${n} .th,.c-${n} .t{fill:${title}}.c-${n} .ts{fill:${sub}}`
    );
  }).join('');

export function buildBaseCss(theme = EMBED_THEME) {
  if (theme === 'dark') return `:root{${LIGHT_VARS}${DARK_VARS}}${COMMON_CSS}${rampRules(true)}`;
  if (theme === 'light') return `:root{${LIGHT_VARS}}${COMMON_CSS}${rampRules(false)}`;
  return (
    `:root{${LIGHT_VARS}}@media (prefers-color-scheme:dark){:root{${DARK_VARS}}}` +
    `${COMMON_CSS}${rampRules(false)}@media (prefers-color-scheme:dark){${rampRules(true)}}`
  );
}

/* ── the sandboxed document ─────────────────────────────────────────────────────────── */
const CSP =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; ' +
  "script-src 'unsafe-inline' https://cdnjs.cloudflare.com https://cdn.jsdelivr.net https://unpkg.com https://esm.sh; " +
  "style-src 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com data:; " +
  "img-src data: blob: https:; media-src data: blob: https:; connect-src 'none'\">";

// sendPrompt()/openLink() exist in claude.ai widgets; here they must simply not crash.
const SHIM_JS =
  "window.sendPrompt=function(t){try{parent.postMessage({htmlEmbedPrompt:String(t)},'*')}catch(e){}};" +
  "window.openLink=function(u){try{window.open(u,'_blank','noopener')}catch(e){}};";

// Tells the host page how tall the content is, so the iframe fits it exactly.
const RESIZE_JS =
  "(function(){function s(){var h=Math.ceil(document.body.getBoundingClientRect().height);parent.postMessage({htmlEmbedHeight:h},'*')}" +
  "if(window.ResizeObserver){new ResizeObserver(s).observe(document.body)}addEventListener('load',s);setTimeout(s,60);s()})();";

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** code = a fragment (<style> + <svg> + <script>…) or a complete HTML document */
export function buildSrcDoc(code, { resize = true, title = '' } = {}) {
  const head = `${STRICT_CSP ? CSP : ''}<style>${buildBaseCss()}</style><script>${SHIM_JS}</script>`;
  const tail = resize ? `<script>${RESIZE_JS}</script>` : '';

  if (/<html[\s>]|<!doctype/i.test(code)) {
    let d = code;
    if (/<head(?=[\s>])[^>]*>/i.test(d)) d = d.replace(/<head(?=[\s>])[^>]*>/i, (m) => m + head);
    else if (/<html[^>]*>/i.test(d)) d = d.replace(/<html[^>]*>/i, (m) => `${m}<head>${head}</head>`);
    else d = head + d;
    return /<\/body>/i.test(d) ? d.replace(/<\/body>/i, () => `${tail}</body>`) : d + tail;
  }

  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">' +
    (title ? `<title>${esc(title)}</title>` : '') +
    `${head}</head><body>${code}${tail}</body></html>`
  );
}

/** One animation as a standalone file: double-click the .htm and it plays. */
export const buildStandaloneHtm = (code, title = 'Animation') => buildSrcDoc(code, { resize: false, title });

export function downloadFile(name, text, type = 'text/html') {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── Read-only display: turn every <div data-html-embed> into a live sandboxed iframe.
      Call it after the HTML is in the DOM; it returns a cleanup function. ─────────────── */
export function hydrateEmbeds(root) {
  if (!root) return undefined;
  const frames = [];

  root.querySelectorAll('div[data-html-embed]').forEach((el) => {
    if (el.dataset.hydrated) { const f = el.querySelector('iframe'); if (f) frames.push(f); return; }
    const code = decodeCode(el.getAttribute('data-code') || '');
    if (!code) return;
    el.dataset.hydrated = '1';

    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin:12px 0';

    const f = document.createElement('iframe');
    f.setAttribute('sandbox', 'allow-scripts allow-popups');   // no allow-same-origin: code cannot touch your site
    f.setAttribute('title', 'Embedded animation');
    f.srcdoc = buildSrcDoc(code);
    f.style.cssText = 'width:100%;height:240px;border:0;display:block';

    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = 'Download .htm';
    b.style.cssText = 'font:12px system-ui,sans-serif;margin-top:4px;padding:3px 8px;cursor:pointer';
    b.onclick = () => downloadFile('animation.htm', buildStandaloneHtm(code));

    wrap.append(f, b);
    el.replaceChildren(wrap);
    frames.push(f);
  });

  if (!frames.length) return undefined;
  const onMsg = (e) => {
    const h = e.data && e.data.htmlEmbedHeight;
    if (typeof h !== 'number') return;
    frames.forEach((f) => { if (f.contentWindow === e.source) f.style.height = `${Math.max(40, h)}px`; });
  };
  window.addEventListener('message', onMsg);
  return () => window.removeEventListener('message', onMsg);
}

/* ── Whole answer -> one .htm file. Each embed becomes its own sandboxed iframe, so the
      animations keep running and their styles/ids never clash. ──────────────────────── */
const DOC_CSS =
  'body{font-family:Calibri,Arial,sans-serif;font-size:16px;line-height:1.6;color:#1f1e1d;margin:0;background:#fff}' +
  'main{max-width:860px;margin:0 auto;padding:24px 16px}' +
  'table{border-collapse:collapse;max-width:100%}td,th{border:1px solid #bbb;padding:6px 8px;vertical-align:top}' +
  'img{max-width:100%;height:auto}blockquote{border-left:3px solid #ccc;margin:1em 0;padding-left:1em;color:#555}' +
  'iframe.embed-frame{width:100%;height:320px;border:0;display:block;margin:12px 0}';

const DOC_JS =
  "addEventListener('message',function(e){var h=e.data&&e.data.htmlEmbedHeight;if(typeof h!=='number')return;" +
  "document.querySelectorAll('iframe.embed-frame').forEach(function(f){if(f.contentWindow===e.source)f.style.height=Math.max(40,h)+'px'})});";

export function buildDocumentHtm(html, title = 'Answer') {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  doc.body.querySelectorAll('div[data-html-embed]').forEach((el) => {
    const code = decodeCode(el.getAttribute('data-code') || '');
    const f = doc.createElement('iframe');
    f.className = 'embed-frame';
    f.setAttribute('sandbox', 'allow-scripts allow-popups');
    f.setAttribute('title', 'Embedded animation');
    f.setAttribute('srcdoc', buildSrcDoc(code));
    el.replaceWith(f);
  });
  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">' +
    `<title>${esc(title)}</title><style>${DOC_CSS}</style></head><body><main>${doc.body.innerHTML}</main>` +
    `<script>${DOC_JS}</script></body></html>`
  );
}